import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { interviewersAPI } from '../../utils/api';
import { fetchAllPages } from '../../utils/fetchAllPages';
import toastr from 'toastr';
import { useAuth } from '../../context/AuthContext';
import EvaluationForm from './EvaluationForm';
import LiveMonitor from './LiveMonitor';
import InterviewScheduleView from './InterviewScheduleView';
import InterviewerStats from './InterviewerStats';
import InterviewQueue from './InterviewQueue';
import MyLabPage from '../../pages/MyLabPage';
import './InterviewerDashboard.css';

function InterviewerDashboard({ user }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [assessments, setAssessments] = useState([]);
  const [liveSessions, setLiveSessions] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showEvaluationForm, setShowEvaluationForm] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Extract active tab from URL path
  const getActiveTabFromPath = () => {
    const path = location.pathname;
    if (path.includes('/interviewer/schedule')) return 'schedule';
    if (path.includes('/interviewer/queue')) return 'queue';
    if (path.includes('/interviewer/assessments')) return 'assessments';
    if (path.includes('/interviewer/live')) return 'live';
    if (path.includes('/interviewer/evaluate')) return 'evaluate';
    if (path.includes('/interviewer/lab')) return 'lab';
    return 'schedule';
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromPath());
  const [filter, setFilter] = useState('all'); // 'all' | 'scheduled' | 'in_progress' | 'completed'
  
  // Sync activeTab with URL changes
  useEffect(() => {
    const tabFromPath = getActiveTabFromPath();
    setActiveTab(tabFromPath);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle tab change - update URL to enable back button
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const tabRoutes = {
      stats: '/interviewer/stats',
      schedule: '/interviewer/schedule',
      queue: '/interviewer/queue',
      assessments: '/interviewer/assessments',
      live: '/interviewer/live',
      evaluate: '/interviewer/evaluate',
      lab: '/interviewer/lab'
    };
    if (tabRoutes[tab]) {
      navigate(tabRoutes[tab], { replace: true });
    }
  };

  useEffect(() => {
    fetchAssessments();
    fetchLiveSessions();

    // Poll for live session updates every 5 seconds
    const interval = setInterval(fetchLiveSessions, 5000);

    return () => clearInterval(interval);
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      // Paginated endpoint (20 per page); walk every page.
      const allAssessments = await fetchAllPages(
        (pageParams) => interviewersAPI.getAssignedAssessments({ ...params, ...pageParams }),
        (data) => data.assessments
      );
      setAssessments(allAssessments);
    } catch (error) {
      console.error('Failed to fetch assessments:', error);
      toastr.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveSessions = async () => {
    try {
      const response = await interviewersAPI.getLiveSessions();
      setLiveSessions(response.data.sessions);
    } catch (error) {
      console.error('Failed to fetch live sessions:', error);
    }
  };

  const handleViewAssessment = async (assessmentId) => {
    try {
      const response = await interviewersAPI.getAssessmentDetails(assessmentId);
      setSelectedAssessment(response.data.assessment);
      // Keep on assessments tab but show details view
      setActiveTab('details');
    } catch (error) {
      console.error('Failed to fetch assessment details:', error);
      toastr.error('Failed to load assessment details');
    }
  };

  const handleEvaluate = (assessment) => {
    setSelectedAssessment(assessment);
    setShowEvaluationForm(true);
    handleTabChange('evaluate');
  };

  const handleEvaluationSubmitted = () => {
    setShowEvaluationForm(false);
    setSelectedAssessment(null);
    handleTabChange('assessments');
    fetchAssessments();
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      draft: 'badge-draft',
      scheduled: 'badge-scheduled',
      in_progress: 'badge-in-progress',
      completed: 'badge-completed',
      cancelled: 'badge-cancelled'
    };

    const statusLabels = {
      draft: 'Draft',
      scheduled: 'Scheduled',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };

    return (
      <span className={`status-badge ${statusStyles[status]}`}>
        {statusLabels[status]}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatTime = (seconds) => {
    if (!seconds) return '00:00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="interviewer-dashboard loading">
        <div className="loading-spinner"></div>
        <p>Loading assessments...</p>
      </div>
    );
  }

  return (
    <div className="interviewer-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <h1>Interviewer Dashboard</h1>
            <p className="subtitle">Welcome back, {user.name || user.fullName || user.displayName || user.email || 'User'}</p>
          </div>
          <div className="user-menu">
            {user?.role === 'admin' && (
              <button
                className="btn-logout"
                onClick={() => navigate('/admin/dashboard')}
                style={{ marginRight: '0.5rem' }}
              >
                Admin Panel
              </button>
            )}
            <span className="user-email">{user.email || user.name || user.fullName || user.displayName || 'User'}</span>
            <button className="btn-logout" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="dashboard-tabs">
        <button
          className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => handleTabChange('stats')}
        >
          Performance Stats
        </button>
        <button
          className={`tab ${activeTab === 'schedule' ? 'active' : ''}`}
          onClick={() => handleTabChange('schedule')}
        >
          Interview Schedule
        </button>
        <button
          className={`tab ${activeTab === 'queue' ? 'active' : ''}`}
          onClick={() => handleTabChange('queue')}
        >
          My Queue
        </button>
        {/* Assessments are only assigned to interviewers in rare cases; the tab stays reachable by URL. */}
        {(activeTab === 'assessments' || assessments.length > 0) && (
          <button
            className={`tab ${activeTab === 'assessments' ? 'active' : ''}`}
            onClick={() => handleTabChange('assessments')}
          >
            Assessments
          </button>
        )}
        <button
          className={`tab ${activeTab === 'live' ? 'active' : ''}`}
          onClick={() => handleTabChange('live')}
        >
          Live Sessions ({liveSessions.length})
        </button>
        {showEvaluationForm && (
          <button
            className={`tab ${activeTab === 'evaluate' ? 'active' : ''}`}
            onClick={() => handleTabChange('evaluate')}
          >
            Evaluation
          </button>
        )}
        <button
          className={`tab ${activeTab === 'lab' ? 'active' : ''}`}
          onClick={() => handleTabChange('lab')}
        >
          My Lab
        </button>
      </nav>

      {/* Content Area */}
      <div className="dashboard-content">
        {/* Performance Stats Tab */}
        {activeTab === 'stats' && (
          <InterviewerStats user={user} />
        )}

        {/* Interview Schedule Tab */}
        {activeTab === 'schedule' && (
          <InterviewScheduleView user={user} />
        )}

        {/* My Queue Tab */}
        {activeTab === 'queue' && (
          <InterviewQueue />
        )}

        {/* Assessments Tab */}
        {activeTab === 'assessments' && (
          <div className="assessments-section">
            {/* Filter Bar */}
            <div className="filter-bar">
              <h2>Assigned Assessments</h2>
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                  onClick={() => setFilter('all')}
                >
                  All
                </button>
                <button
                  className={`filter-btn ${filter === 'scheduled' ? 'active' : ''}`}
                  onClick={() => setFilter('scheduled')}
                >
                  Scheduled
                </button>
                <button
                  className={`filter-btn ${filter === 'in_progress' ? 'active' : ''}`}
                  onClick={() => setFilter('in_progress')}
                >
                  In Progress
                </button>
                <button
                  className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
                  onClick={() => setFilter('completed')}
                >
                  Completed
                </button>
              </div>
            </div>

            {/* Assessments List */}
            <div className="assessments-grid">
              {assessments.length === 0 ? (
                <div className="empty-state">
                  <p>No assessments found</p>
                </div>
              ) : (
                assessments.map((assessment) => (
                  <div key={assessment._id} className="assessment-card">
                    <div className="card-header">
                      <h3>{assessment.name}</h3>
                      {getStatusBadge(assessment.status)}
                    </div>

                    <div className="card-body">
                      <div className="info-row">
                        <span className="label">Candidate:</span>
                        <span className="value">{assessment.candidateId?.name || 'N/A'}</span>
                      </div>
                      <div className="info-row">
                        <span className="label">Position:</span>
                        <span className="value">{assessment.position}</span>
                      </div>
                      <div className="info-row">
                        <span className="label">Mode:</span>
                        <span className="value">{assessment.mode}</span>
                      </div>
                      {assessment.scheduling?.availableFrom && (
                        <div className="info-row">
                          <span className="label">Available:</span>
                          <span className="value">
                            {formatDate(assessment.scheduling.availableFrom)}
                          </span>
                        </div>
                      )}
                      {assessment.results?.overallScore !== null && (
                        <div className="info-row">
                          <span className="label">Score:</span>
                          <span className="value score">{assessment.results.overallScore}%</span>
                        </div>
                      )}
                    </div>

                    <div className="card-actions">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleViewAssessment(assessment._id)}
                      >
                        View Details
                      </button>
                      {assessment.status === 'completed' && !assessment.interviewerEvaluation?.evaluatedAt && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleEvaluate(assessment)}
                        >
                          Submit Evaluation
                        </button>
                      )}
                      {assessment.interviewerEvaluation?.evaluatedAt && (
                        <span className="evaluated-badge">Evaluated</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Live Sessions Tab */}
        {activeTab === 'live' && (
          <LiveMonitor sessions={liveSessions} formatTime={formatTime} />
        )}

        {/* Evaluation Form Tab */}
        {activeTab === 'evaluate' && showEvaluationForm && selectedAssessment && (
          <EvaluationForm
            assessment={selectedAssessment}
            onSubmitted={handleEvaluationSubmitted}
            onCancel={() => {
              setShowEvaluationForm(false);
              handleTabChange('assessments');
            }}
          />
        )}

        {/* Assessment Details View */}
        {activeTab === 'details' && selectedAssessment && (
          <div className="assessment-details">
            <div className="details-header">
              <h2>{selectedAssessment.name}</h2>
            <button
              className="btn btn-secondary"
              onClick={() => handleTabChange('assessments')}
            >
              Back to List
            </button>
            </div>

            <div className="details-grid">
              <div className="details-section">
                <h3>Candidate Information</h3>
                <div className="info-row">
                  <span className="label">Name:</span>
                  <span className="value">{selectedAssessment.candidateId?.name}</span>
                </div>
                <div className="info-row">
                  <span className="label">Email:</span>
                  <span className="value">{selectedAssessment.candidateId?.email}</span>
                </div>
                {selectedAssessment.candidateId?.phone && (
                  <div className="info-row">
                    <span className="label">Phone:</span>
                    <span className="value">{selectedAssessment.candidateId.phone}</span>
                  </div>
                )}
              </div>

              <div className="details-section">
                <h3>Assessment Configuration</h3>
                <div className="info-row">
                  <span className="label">Position:</span>
                  <span className="value">{selectedAssessment.position}</span>
                </div>
                <div className="info-row">
                  <span className="label">Mode:</span>
                  <span className="value">{selectedAssessment.mode}</span>
                </div>
                <div className="info-row">
                  <span className="label">Status:</span>
                  {getStatusBadge(selectedAssessment.status)}
                </div>
              </div>

              {selectedAssessment.results && selectedAssessment.status === 'completed' && (
                <div className="details-section">
                  <h3>Results</h3>
                  <div className="info-row">
                    <span className="label">Overall Score:</span>
                    <span className="value score">{selectedAssessment.results.overallScore}%</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Status:</span>
                    <span className={`value ${selectedAssessment.results.passed ? 'passed' : 'failed'}`}>
                      {selectedAssessment.results.passed ? 'PASSED' : 'FAILED'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label">Flags Correct:</span>
                    <span className="value">
                      {selectedAssessment.results.flagsCorrect} / {selectedAssessment.results.flagsSubmitted}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label">Hints Used:</span>
                    <span className="value">{selectedAssessment.results.hintsUsed}</span>
                  </div>
                </div>
              )}
            </div>

            {selectedAssessment.status === 'completed' && !selectedAssessment.interviewerEvaluation?.evaluatedAt && (
              <div className="details-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => handleEvaluate(selectedAssessment)}
                >
                  Submit Evaluation
                </button>
              </div>
            )}
          </div>
        )}
        {/* My Lab Tab */}
        {activeTab === 'lab' && (
          <MyLabPage />
        )}
      </div>
    </div>
  );
}

export default InterviewerDashboard;
